import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { CMS_BASE_URL } from 'config';
import { coursesService } from 'services/courses';
import { usersActions } from 'users/actions';
import PaginationBlock from 'common/pagination';
import PaginationLoader from 'components/loader/paginationLoader';
import TableResultSelection from 'common/table-results-selection';
import FormatDate from 'common/format-date';
import { TableBlock } from 'assets/styles/table-styles';
import { SearchField, ButtonsHolder } from 'users/styled';
import { DataBlock, SearchTagsList } from 'analytics/styled';
import { EMPTY_LIST_PLACEHOLDER } from 'utils/values';
import { messageNotification } from 'utils/functions';
import { CoursesWrapper, ModalOverlay } from './styled';

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_FORM = { name: '', number: '', run: '' };

const Courses = ({
  isStaff, isSuperuser, organization, organizations, selectedOrg, dispatch,
}) => {
  const canSelectOrg = isStaff || isSuperuser;

  const [courses, setCourses] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inProgress, setProgress] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOrg, setFormOrg] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchCourses = (pageNumber, searchValue, status, size = pageSize) => {
    setProgress(true);
    const params = { page: pageNumber, page_size: size };
    if (searchValue) params.search = searchValue;
    if (status && status !== 'all') params.status = status;
    coursesService
      .fetchCourses(params)
      .then((response) => {
        setCourses(response.data.results || []);
        setCount(response.data.count || 0);
        setProgress(false);
      })
      .catch(() => {
        setCourses([]);
        setCount(0);
        setProgress(false);
      });
  };

  useEffect(() => {
    fetchCourses(1, '', 'all');
    if (canSelectOrg && organizations.length === 0) {
      dispatch(usersActions.fetchOrganizations());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(1);
      fetchCourses(1, search, statusFilter);
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(1);
    fetchCourses(1, search, value);
  };

  const handlePageChange = (property, value) => {
    setPage(value);
    fetchCourses(value, search, statusFilter);
  };

  const handlePageSize = (size) => {
    setPageSize(size);
    setPage(1);
    fetchCourses(1, search, statusFilter, size);
  };

  const handleClear = () => {
    setSearch('');
    setStatusFilter('all');
    setPage(1);
    fetchCourses(1, '', 'all');
  };

  const openModal = () => {
    setForm(EMPTY_FORM);
    setFormOrg(canSelectOrg ? (selectedOrg || '') : (organization || ''));
    setFormError('');
    setShowModal(true);
  };

  const handleCreate = () => {
    const name = form.name.trim();
    const number = form.number.trim();
    const run = form.run.trim();
    if (!name || !number || !run) {
      setFormError('Course name, number, and run are all required.');
      return;
    }
    if (canSelectOrg && !formOrg) {
      setFormError('Please select an organization.');
      return;
    }
    setSaving(true);
    setFormError('');
    const payload = { name, number, run };
    if (canSelectOrg) payload.org = formOrg;
    coursesService
      .createCourse(payload)
      .then(() => {
        setSaving(false);
        setShowModal(false);
        messageNotification('success', 'Success', 'Course shell created successfully.');
        setStatusFilter('all');
        setSearch('');
        setPage(1);
        fetchCourses(1, '', 'all');
      })
      .catch((error) => {
        setSaving(false);
        const msg = (error.response && error.response.data && error.response.data.error)
          || 'Unable to create the course. Please try again.';
        setFormError(msg);
      });
  };

  return (
    <CoursesWrapper>
      <div className="courses-header">
        <h1>Courses</h1>
        <button type="button" onClick={openModal}>
          <span className="icon-Plus icon" /> Create Course
        </button>
      </div>

      <div className="courses-filters">
        <SearchField className="courses-search">
          <span className="search-icon icon-Search-2-Outline-Color" />
          <input
            type="text"
            className="form-control"
            placeholder="Search courses"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchField>
        <select
          className="form-control status-select"
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <button type="button" className="clear-btn" onClick={handleClear}>Clear</button>
      </div>

      <DataBlock className="v2">
        <SearchTagsList />
        <TableResultSelection handleOnChange={handlePageSize} pageSize={DEFAULT_PAGE_SIZE} />
      </DataBlock>

      { inProgress && <PaginationLoader /> }

      <TableBlock className="course-table responsive">
        <table>
          <thead>
            <tr>
              <th>Course Name</th>
              <th>Organization</th>
              <th>Number</th>
              <th>Run</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((course) => (
              <tr key={course.id}>
                <td data-title="Course Name">
                  <a
                    href={`${CMS_BASE_URL}course/${course.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {course.display_name || course.id}
                  </a>
                </td>
                <td data-title="Organization">{course.org}</td>
                <td data-title="Number">{course.number}</td>
                <td data-title="Run">{course.run}</td>
                <td data-title="Start">
                  {course.start_date
                    ? <FormatDate format="Do MMM YYYY">{course.start_date}</FormatDate>
                    : EMPTY_LIST_PLACEHOLDER}
                </td>
                <td data-title="End">
                  {course.end_date
                    ? <FormatDate format="Do MMM YYYY">{course.end_date}</FormatDate>
                    : EMPTY_LIST_PLACEHOLDER}
                </td>
                <td data-title="Status">
                  <span className={`status-tag ${course.status}`}>{course.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableBlock>

      {courses.length === 0 && !inProgress && (
        <div className="no-data">No courses to show</div>
      )}

      {count > 0 && (
        <PaginationBlock
          activePage={page}
          itemsCountPerPage={pageSize}
          totalItemsCount={count}
          pageRangeDisplayed={5}
          onPageChange={handlePageChange}
          showPageCounter
        />
      )}

      {showModal && (
        <ModalOverlay>
          <div className="modal-card">
            <h2>Create Course</h2>

            <label htmlFor="course-name">
              <span>Course Name *</span>
              <input
                id="course-name"
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>

            <label htmlFor="course-number">
              <span>Course Number *</span>
              <input
                id="course-number"
                type="text"
                className="form-control"
                value={form.number}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
              />
            </label>

            <label htmlFor="course-run">
              <span>Course Run *</span>
              <input
                id="course-run"
                type="text"
                className="form-control"
                value={form.run}
                onChange={(e) => setForm({ ...form, run: e.target.value })}
              />
            </label>

            <label htmlFor="course-org">
              <span>Organization *</span>
              {canSelectOrg ? (
                <select
                  id="course-org"
                  className="form-control"
                  value={formOrg}
                  onChange={(e) => setFormOrg(e.target.value)}
                >
                  <option value="">Select an Organization</option>
                  {organizations.map((org) => (
                    <option key={org.short_name} value={org.short_name}>{org.short_name}</option>
                  ))}
                </select>
              ) : (
                <input id="course-org" type="text" className="form-control" value={formOrg} disabled />
              )}
            </label>

            {formError && <div className="field-error">{formError}</div>}

            <ButtonsHolder>
              <button type="button" className="group" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
                <i className="icon right icon-Cancel" />
              </button>
              <button type="button" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create'}
                <i className="icon right icon-Check" />
              </button>
            </ButtonsHolder>
          </div>
        </ModalOverlay>
      )}
    </CoursesWrapper>
  );
};

function mapStateToProps(state) {
  return {
    isStaff: state.LOGGEDINUSER.isStaff,
    isSuperuser: state.LOGGEDINUSER.isSuperuser,
    organization: state.LOGGEDINUSER.organization || '',
    organizations: state.LOGGEDINUSER.organizations || [],
    selectedOrg: state.LOGGEDINUSER.selectedOrg || '',
  };
}

export default connect(mapStateToProps)(Courses);
